import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { Level, Label, Tab } from 'rbx';
import { withFormik } from 'formik';
import { useStoreState } from 'easy-peasy';
import { isNull, isNaN } from 'lodash';
import swal from 'sweetalert';
import { toast } from 'react-toastify';

import uuidv4 from 'uuid/v4';

import Layout from '../../../components/global/Layout';
import {
  Input,
  TextArea,
  Radio,
  CheckBox,
  ReactDateTimePicker,
  MobileInput,
  Select,
  TagInputs,
  InputErrorMessage,
  Tabs,
} from '../../../components/elements';
import Header from './Header';
import { useTableBookingsDetails } from '../TableBookingManager/helpers';
import Footer from './Footer';
import {
  useCreateTableBooking,
  usePlaceData,
  useUpdateTableBooking,
} from './helpers';
import BookingSlotsList from './BookingSlotsList';
import {
  useTableSetting,
  useTableSettingPrivate,
} from '../TableBookingSetting/helpers';
import PaymentsTab from './PaymentsTab';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 480px 480px;
  gap: 10px;
  padding: 10px;
`;

const GridItem = styled.div`
  padding: 5px 20px;
  text-align: left;
`;

const ActionButtonsContainer = styled.div`
  position: relative;
  left: 40px;
  width: 700px;
  display: flex;
  flex-direction: row;
  padding: 0 0 30px 0;
`;

const Gap = styled.div`
  margin: 0 1rem;
`;

const isPostMidnightTime = timeStr => {
  const match = timeStr && timeStr.match(/^(\d+):\d+ AM$/i);
  if (!match) return false;
  const hour = parseInt(match[1], 10);
  return hour === 12 || hour <= 5;
};

const parseData = parseValue => {
  const final = [];
  parseValue.forEach(item => {
    final.push({ value: item, label: item });
  });
  return final;
};

const parseInput = parseValue => {
  const final = [];
  (parseValue ?? []).forEach(item => {
    final.push(item.value);
  });
  return final;
};

const getTabs = bookingFee => {
  if (bookingFee) {
    return [
      {
        key: 'Booking',
        name: 'Booking',
        index: 0,
      },
      {
        key: 'Payments',
        name: 'Payments',
        index: 1,
      },
    ];
  }

  return [
    {
      key: 'Booking',
      name: 'Booking',
      index: 0,
    },
  ];
};

const Form = ({ ...props }) => {
  const {
    values,
    tableBookingDataProps,
    isSubmitting,
    handleSubmit,
    history,
    setFieldValue,
    errors,
    touched,
    updateTableBooking,
    updateTableBookingError,
    tableSettingProps,
    isAdd,
  } = props;

  const [activeTab, setActiveTab] = React.useState(0);

  const { userId: adminUserId } = useStoreState(state => state.auth);

  const { placeId, tableBookingId } = props;

  // const settingsSmsValue =
  //   props?.tableSettingProps?.notification_setting?.sms_user_confirm_booking;

  const tempDate2 = new Date(values.booking_date);

  // const minDate = React.useRef(values.booking_date);

  const onChangeBookingStatus = async bookingStatus =>
    updateTableBooking({
      user_id: adminUserId,
      table_booking_id: tableBookingDataProps.table_booking_id,
      booking_status: bookingStatus,
      is_sms_enabled: values.smsNotif,
      is_email_enabled: values.emailNotif,
    });

  const onChangeGuestStatus = async guestStatus =>
    updateTableBooking({
      user_id: adminUserId,
      table_booking_id: tableBookingDataProps.table_booking_id,
      guest_status: guestStatus,
      booking_status: 'MODIFIED',
      is_sms_enabled: values.smsNotif,
      is_email_enabled: values.emailNotif,
    });

  const tableDetailsOptions = (tableSettingProps?.table_details ?? []).map(
    ele => ({
      label: `${ele.table_name} ${ele.table_number} - ${ele.table_capacity}`,
      value: ele.table_number,
    }),
  );

  const tagOptions = Array.from(
    new Set([...values.tags, ...(tableSettingProps?.tag ?? [])]),
  ).map(ele => ({
    label: ele,
    value: ele,
  }));

  const placeData = usePlaceData({ adminUserId, placeId });

  // React.useEffect(() => {
  //   setFieldValue('smsNotif', settingsSmsValue);
  // }, [settingsSmsValue]);

  React.useEffect(() => {
    if (updateTableBookingError) {
      toast.error(updateTableBookingError);
    }
  }, [updateTableBookingError]);

  // if (updatingTableBooking){
  //   return <updatingTableBooking />
  // }

  const collectBookingFee = !!tableBookingDataProps?.billing?.booking_total;

  const tabs = getTabs(collectBookingFee);

  React.useEffect(() => {
    setFieldValue(
      'emailNotif',
      tableSettingProps.isEmailNotificationCheckboxEnabled,
    );
    setFieldValue(
      'smsNotif',
      tableSettingProps.isSMSNotificationCheckboxEnabled,
    );
  }, [
    tableSettingProps.isEmailNotificationCheckboxEnabled,
    tableSettingProps.isSMSNotificationCheckboxEnabled,
  ]);

  return (
    <Layout>
      <Header
        history={history}
        isAdd={isAdd}
        place={placeData}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        bookingReference={tableBookingDataProps?.booking_reference}
      />
      <Tabs className="tabs is-medium">
        {tabs.map(tab => (
          <Tab
            active={activeTab === tab.index}
            key={tab.key}
            onClick={() => setActiveTab(tab.index)}
          >
            {tab.name}
          </Tab>
        ))}
      </Tabs>
      {activeTab === 0 && (
        <>
          <GridContainer>
            <GridItem>
              <div className="is-flex">
                <div style={{ padding: '5px 10px' }}>
                  <Label>Booking Date</Label>
                </div>
                <ReactDateTimePicker
                  value={new Date(values.booking_date)}
                  onChange={value => {
                    setFieldValue('booking_date', value);
                    setFieldValue('slotDate', null);
                  }}
                  minDate={new Date()}
                />
              </div>
            </GridItem>
            <GridItem>
              <div className="is-flex">
                <div style={{ padding: '5px 10px' }}>
                  <Label>Booking Status</Label>
                </div>
                <div
                  style={{
                    width: '150px',
                    position: 'relative',
                    bottom: '4px',
                  }}
                >
                  <Select
                    options={[]}
                    value={[
                      {
                        label: values.booking_status,
                        value: values.booking_status,
                      },
                    ]}
                    disableToastMessage
                    disabled
                  />
                </div>
              </div>
            </GridItem>
            <GridItem />
          </GridContainer>
          {!isAdd && (
            <ActionButtonsContainer>
              {values.paymentCaptured && collectBookingFee && (
                <>
                  <button
                    className="button is-grey is-outlined"
                    onClick={() => {
                      if (values.booking_status === 'RECEIVED') {
                        swal({
                          text: 'Please confirm the table booking',
                        });
                      } else {
                        swal({
                          text: 'Would you like to refund this table booking?',
                          buttons: ['Cancel', 'OK'],
                        }).then(res => {
                          if (res) {
                            history.push(
                              `/refund-booking/${placeId}/${tableBookingId}`,
                            );
                          }
                        });
                      }
                    }}
                  >
                    Refund Booking
                  </button>
                  <Gap />
                </>
              )}
              <button
                className="button is-grey is-outlined"
                disabled={values.booking_status === 'CANCELLED'}
                onClick={() => {
                  swal({
                    text: 'Would you like to cancel this table booking?',
                    buttons: ['Cancel', 'OK'],
                  }).then(res => {
                    if (res) {
                      history.push(
                        `/cancel-booking/${placeId}/${tableBookingId}`,
                      );
                    }
                  });
                }}
              >
                Cancel Booking
              </button>
              <Gap />
              <button
                className="button is-warning is-outlined"
                disabled={values.booking_status === 'WAITLISTED'}
                onClick={() => {
                  if (values.booking_status === 'WAITLISTED') {
                    // pass
                  } else {
                    swal({
                      text: 'Would you like to waitlist this table booking?',
                      buttons: ['Cancel', 'OK'],
                    }).then(res => {
                      if (res) {
                        onChangeBookingStatus('WAITLISTED');
                        console.log('Waitlisting...');
                      }
                    });
                  }
                }}
              >
                Waitlist Booking
              </button>
              <Gap />
              {values.booking_status === 'CONFIRMED' && (
                <>
                  <button
                    onClick={() => {
                      if (values.booking_status === 'CONFIRMED') {
                        // pass
                      } else {
                        swal({
                          text: 'Would you like to confirm this table booking?',
                          buttons: ['Cancel', 'OK'],
                        }).then(res => {
                          if (res) {
                            onChangeBookingStatus('CONFIRMED');
                            console.log('confirming...');
                          }
                        });
                      }
                    }}
                    className="button is-outlined is-primary"
                  >
                    Resend Confirmation
                  </button>
                  <Gap />
                </>
              )}
              {values.booking_status !== 'CONFIRMED' && (
                <>
                  <button
                    className="button is-primary is-outlined"
                    disabled={values.booking_status === 'CONFIRMED'}
                    onClick={() => {
                      if (values.booking_status === 'CONFIRMED') {
                        // pass
                      } else {
                        swal({
                          text: 'Would you like to confirm this table booking?',
                          buttons: ['Cancel', 'OK'],
                        }).then(res => {
                          if (res) {
                            onChangeBookingStatus('CONFIRMED');
                            console.log('confirming...');
                          }
                        });
                      }
                    }}
                  >
                    Confirm Booking
                  </button>
                  <Gap />
                </>
              )}
            </ActionButtonsContainer>
          )}
          <div className="is-flex" style={{ padding: '10px' }}>
            <div style={{ padding: '0 20px' }}>
              <div
                style={{ padding: '10px', position: 'relative', top: '-8px' }}
              >
                <Label>Guest Status</Label>
              </div>
            </div>
            <div style={{ padding: '0 25px' }}>
              <div className="is-flex">
                <Level.Item>
                  <div style={{ padding: '0 20px', fontWeight: '800' }}>
                    <Radio
                      stopClickFn
                      label="UPCOMING"
                      value={values.guest_status === 'UPCOMING'}
                      onChange={() => {
                        if (isAdd) {
                          setFieldValue('guest_status', 'UPCOMING');
                        } else {
                          swal({
                            text:
                              'Would you like to UPCOMING this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              onChangeGuestStatus('UPCOMING');
                              console.log('UPCOMING...');
                            }
                          });
                        }
                      }}
                    />
                  </div>
                </Level.Item>
                &nbsp;
                <Level.Item>
                  <div style={{ padding: '0 20px', fontWeight: '800' }}>
                    <Radio
                      stopClickFn
                      label="CHECKED IN"
                      value={values.guest_status === 'CHECKEDIN'}
                      onChange={() => {
                        if (isAdd) {
                          swal({
                            text:
                              'Would you like to CHECKED IN this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              setFieldValue('guest_status', 'CHECKEDIN');
                            }
                          });
                        } else {
                          swal({
                            text:
                              'Would you like to CHECKED IN this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              onChangeGuestStatus('CHECKEDIN');

                              console.log('CHECKEDIN...');
                            }
                          });
                        }
                      }}
                    />
                  </div>
                </Level.Item>
                &nbsp;
                <Level.Item>
                  <div style={{ padding: '0 20px', fontWeight: '800' }}>
                    <Radio
                      stopClickFn
                      label="SEATED"
                      value={values.guest_status === 'SEATED'}
                      onChange={() => {
                        if (isAdd) {
                          swal({
                            text:
                              'Would you like to SEATED this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              setFieldValue('guest_status', 'SEATED');
                            }
                          });
                        } else {
                          swal({
                            text:
                              'Would you like to SEATED this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              onChangeGuestStatus('SEATED');
                              console.log('SEATED...');
                            }
                          });
                        }
                      }}
                    />
                  </div>
                </Level.Item>
                &nbsp;
                <Level.Item>
                  <div style={{ padding: '0 20px', fontWeight: '800' }}>
                    <Radio
                      stopClickFn
                      label="NO SHOW"
                      value={values.guest_status === 'NO SHOW'}
                      onChange={() => {
                        if (isAdd) {
                          swal({
                            text:
                              'Would you like to NO SHOW this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              setFieldValue('guest_status', 'NO SHOW');
                            }
                          });
                        } else {
                          swal({
                            text:
                              'Would you like to NO SHOW this table booking?',
                            buttons: ['Cancel', 'OK'],
                          }).then(res => {
                            if (res) {
                              onChangeGuestStatus('NO SHOW');
                              console.log('NOSHOW...');
                            }
                          });
                        }
                      }}
                    />
                  </div>
                </Level.Item>
                &nbsp;
              </div>
            </div>
          </div>
          <div className="is-flex" style={{ padding: '10px' }}>
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-3px' }}
                >
                  Number of Guests <span className="has-text-danger">*</span>
                </Label>
                <Input
                  value={values.guest_count}
                  onChange={e => setFieldValue('guest_count', e.target.value)}
                  disableToastMessage
                  required
                  errors={errors.guest_count}
                  touched={touched.guest_count}
                />
              </div>
            </div>
            <div style={{ padding: '20px', width: '60%' }}>
              <div
                className="is-flex"
                style={{
                  padding: '10px',
                  position: 'relative',
                  top: '-32px',
                }}
              >
                <div style={{ width: '400px' }}>
                  <Select
                    label="Table Number"
                    options={tableDetailsOptions}
                    value={(() =>
                      tableDetailsOptions.find(
                        tdo => tdo.value === values.table_number,
                      ))()}
                    onChange={value =>
                      setFieldValue('table_number', value.value)
                    }
                    disableToastMessage
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ position: 'relative', top: '-2rem' }}>
            <div className="is-flex">
              <div style={{ padding: '0 40px' }}>
                <Label>
                  Booking Time <span className="has-text-danger">*</span>
                </Label>
                <InputErrorMessage
                  errors={errors.booking_time}
                  touched={touched.booking_time}
                  disableToastMessage
                />
              </div>
              <div>
                <Label>{values.booking_time}</Label>
              </div>
            </div>
            <BookingSlotsList
              isAdd={isAdd}
              placeId={placeId}
              onSlotClick={slot => {
                const isMidnight =
                  slot.greeting === 'Midnight' || isPostMidnightTime(slot.time);
                if (isMidnight) {
                  const nextDate =
                    slot.date ||
                    (() => {
                      const d = new Date(tempDate2);
                      d.setDate(d.getDate() + 1);
                      return d.toISOString().slice(0, 10);
                    })();
                  setFieldValue('slotDate', nextDate);
                } else {
                  setFieldValue('slotDate', null);
                }
                setFieldValue('booking_time', slot.time);
              }}
              serviceTypeSettingId={
                props?.tableSettingProps?.service_type_setting_id
              }
              booking_time={values.booking_time}
              date={tempDate2}
            />
          </div>
          <div
            className="is-flex"
            style={{ padding: '0 10px', marginTop: '-12px' }}
          >
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-4px' }}
                >
                  Guest First Name <span className="has-text-danger">*</span>
                </Label>
                <Input
                  value={values.first_name}
                  onChange={e => setFieldValue('first_name', e.target.value)}
                  disableToastMessage
                  errors={errors.first_name}
                  touched={touched.first_name}
                  required
                />
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-4px' }}
                >
                  Guest Last Name <span className="has-text-danger">*</span>
                </Label>
                <Input
                  value={values.last_name}
                  onChange={e => setFieldValue('last_name', e.target.value)}
                  disableToastMessage
                  errors={errors.last_name}
                  touched={touched.last_name}
                  required
                />
              </div>
            </div>
          </div>
          <div
            className="is-flex"
            style={{ padding: '10px', marginTop: '-12px' }}
          >
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-4px' }}
                >
                  Email <span className="has-text-danger">*</span>
                </Label>
                <Input
                  value={values.contact_email}
                  onChange={e => setFieldValue('contact_email', e.target.value)}
                  disableToastMessage
                  errors={errors.contact_email}
                  touched={touched.contact_email}
                  required
                />
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-4px' }}
                >
                  Phone <span className="has-text-danger">*</span>
                </Label>
                <MobileInput
                  name="contact_phone"
                  // label="Primary Contact Phone"
                  value={values.contact_phone}
                  errors={errors.contact_phone}
                  touched={touched.contact_phone}
                  // onChange={value => setFieldValue('primaryMobile', value)}
                  required
                  onChange={e => setFieldValue('contact_phone', e)}
                  disableToastMessage
                />
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="is-flex">
                <Label
                  style={{ padding: '10px', position: 'relative', top: '-4px' }}
                >
                  Source
                </Label>
                <Input value={values.platform} disabled />
              </div>
            </div>
          </div>
          <div style={{ padding: '0 40px' }}>
            <Label>Guest Requests</Label>
            <TextArea
              value={values.guest_notes}
              onChange={e => setFieldValue('guest_notes', e.target.value)}
              name="Guest Requests"
              errors={errors.guest_notes}
              touched={touched.guest_notes}
              optionalText="(Max 144 Characters)"
            />
            {values.guest_notes && (
              <div style={{ textAlign: 'right' }}>
                {values.guest_notes.length > 144 ||
                values.guest_notes.length < 1
                  ? ''
                  : `Characters left ${144 - values.guest_notes.length}`}
              </div>
            )}

            <br />

            <Label>Restaurant Notes</Label>
            <TextArea
              value={values.restaurant_notes}
              onChange={e => setFieldValue('restaurant_notes', e.target.value)}
              name="Restaurant Requests"
              errors={errors.restaurant_notes}
              touched={touched.restaurant_notes}
              optionalText="(Max 144 Characters)"
            />
            {values.restaurant_notes && (
              <div style={{ textAlign: 'right' }}>
                {values.restaurant_notes.length > 144 ||
                values.restaurant_notes.length < 1
                  ? ''
                  : `Characters left ${144 - values.restaurant_notes.length}`}
              </div>
            )}
          </div>
          <br />
          <div className="is-flex" style={{ padding: '0 10px' }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <div className="is-flex">
                <Label
                  style={{
                    padding: '10px',
                    position: 'relative',
                    bottom: '11px',
                  }}
                >
                  Email Notification
                </Label>
                <CheckBox
                  value={values.emailNotif}
                  onChange={e => {
                    setFieldValue('emailNotif', e);
                  }}
                />
              </div>
            </div>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <div className="is-flex">
                <Label
                  style={{
                    padding: '10px',
                    position: 'relative',
                    bottom: '11px',
                  }}
                >
                  SMS Notification
                </Label>
                <CheckBox
                  value={values.smsNotif}
                  onChange={e => {
                    setFieldValue('smsNotif', e);
                  }}
                />
              </div>
            </div>
          </div>
          <br />
          <div className="is-flex" style={{ padding: '0 10px' }}>
            <div
              style={{
                padding: '0 20px',
                marginBottom: '60px',
                marginLeft: '10px',
              }}
            >
              <div style={{ width: '500px' }}>
                <TagInputs
                  label="Tags / Occasion"
                  value={parseData(values.tags)}
                  suggestions={tagOptions}
                  onChange={value => {
                    setFieldValue('tags', parseInput(value));
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {activeTab === 1 && (
        <PaymentsTab
          tableBookingDataProps={tableBookingDataProps}
          tableSettingProps={tableSettingProps}
          refundSummary={values.refundSummary}
        />
      )}
      <Footer
        isAdd={isAdd}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        history={history}
      />
    </Layout>
  );
};

const transformRefundAmount = val => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : Math.abs(num);
};

const AddModifyBookingForm = withFormik({
  mapPropsToValues: ({ tableBookingDataProps, tableSettingProps }) => {
    const refundsDetails =
      tableBookingDataProps?.payment?.refunds_details &&
      Array.isArray(tableBookingDataProps?.payment?.refunds_details)
        ? tableBookingDataProps?.payment?.refunds_details
        : [];

    const refundSummary = [];

    refundsDetails.forEach(ele => {
      const ra = transformRefundAmount(ele.refund_amount);

      const temp = {
        userId: ele.attempted_by,
        refundAmount: ra,
        refundType: ele.refund_type,
        refundedAt: ele.refunded_at,
        status: ele.status,
        refundReason: ele.refund_reason,
        note: ele.note,
      };

      refundSummary.push(temp);
    });

    const paymentCaptured =
      tableBookingDataProps?.payment?.payment_state === 'CAPTURED';

    return {
      paymentCaptured,
      contact_email:
        tableBookingDataProps &&
        !isNull(tableBookingDataProps?.contact?.contact_email)
          ? tableBookingDataProps.contact.contact_email
          : '',

      contact_phone:
        tableBookingDataProps &&
        !isNull(tableBookingDataProps?.contact?.contact_phone)
          ? tableBookingDataProps.contact.contact_phone
          : '',

      first_name:
        tableBookingDataProps &&
        !isNull(tableBookingDataProps?.contact?.first_name)
          ? tableBookingDataProps.contact.first_name
          : '',
      last_name:
        tableBookingDataProps &&
        !isNull(tableBookingDataProps?.contact?.last_name)
          ? tableBookingDataProps.contact.last_name
          : '',

      platform:
        tableBookingDataProps && !isNull(tableBookingDataProps?.platform)
          ? tableBookingDataProps.platform
          : 'ADMINUSER',

      guest_notes:
        tableBookingDataProps && !isNull(tableBookingDataProps.guest_notes)
          ? tableBookingDataProps.guest_notes
          : '',
      restaurant_notes:
        tableBookingDataProps && !isNull(tableBookingDataProps.restaurant_notes)
          ? tableBookingDataProps.restaurant_notes
          : '',
      tags:
        tableBookingDataProps && !isNull(tableBookingDataProps.tags)
          ? tableBookingDataProps.tags
          : [],

      booking_time:
        tableBookingDataProps && !isNull(tableBookingDataProps.booking_time)
          ? tableBookingDataProps.booking_time
          : '',
      guest_count:
        tableBookingDataProps && !isNull(tableBookingDataProps?.guest_count)
          ? tableBookingDataProps.guest_count
          : '',

      table_number:
        tableBookingDataProps && !isNull(tableBookingDataProps?.table_number)
          ? tableBookingDataProps.table_number
          : '',

      booking_date:
        tableBookingDataProps && !isNull(tableBookingDataProps?.booking_date)
          ? [
              tableBookingDataProps.booking_date.split('-')[1],
              tableBookingDataProps.booking_date.split('-')[0],
              tableBookingDataProps.booking_date.split('-')[2],
            ].join('-')
          : new Date(),

      booking_status:
        tableBookingDataProps && !isNull(tableBookingDataProps.booking_status)
          ? tableBookingDataProps.booking_status
          : 'CONFIRMED',

      guest_status:
        tableBookingDataProps && !isNull(tableBookingDataProps?.guest_status)
          ? tableBookingDataProps.guest_status
          : 'UPCOMING',
      emailNotif: tableSettingProps.isEmailNotificationCheckboxEnabled,
      smsNotif: tableSettingProps.isSMSNotificationCheckboxEnabled,
      slotDate: null,
      refundSummary,
    };
  },

  validationSchema: yup.object().shape({
    contact_email: yup
      .string()
      .required('Email is required')
      .email('Invalid email format'),
    contact_phone: yup
      .number()
      .min(12, 'Please enter a valid mobile number')
      .required('Phone number is required'),
    // .max(13, 'Please enter a valid mobile number')
    booking_time: yup.string().required('Booking time is required'),
    guest_count: yup
      .number()
      .typeError('Please enter a valid number')
      .integer('Decimals are not allowed')
      .min(1, 'Guest Count must be at least 1')
      .max(999, 'Guest Count cannot exceed 999')
      .test(
        'maxDigits',
        'Guest Count must have at most 3 digits',
        value => value !== undefined && value.toString().length <= 3,
      )
      .required('Guest Count is required'),

    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    guest_notes: yup
      .string()
      .max(144, 'Notes must be 144 characters or less')
      .test(
        'not-all-space',
        'Notes cannot be empty or contain only spaces',
        value => (value ? value.trim().length > 0 : true),
      ),
    restaurant_notes: yup
      .string()
      .max(144, 'Notes must be 144 characters or less')
      .test(
        'not-all-space',
        'Notes cannot be empty or contain only spaces',
        value => (value ? value.trim().length > 0 : true),
      ),
  }),
  validateOnBlur: false,
  validateOnChange: false,
  handleSubmit: async (values, { setSubmitting, props }) => {
    setSubmitting(true);

    const { createTableBooking, isAdd, placeId, history } = props;
    const {
      contact_email,
      contact_phone,
      first_name,
      last_name,
      booking_date,
      booking_status,
      booking_time,
      guest_count,
      guest_notes,
      guest_status,
      table_number,
      tags,
      restaurant_notes,
      platform,
    } = values;

    if (isAdd) {
      const input = {
        contact: {
          contact_email,
          contact_phone,
          first_name,
          last_name,
        },
        restaurant_notes,

        booking_date: values.slotDate
          ? values.slotDate
              .split('-')
              .reverse()
              .join('-')
          : new Date(booking_date)
              .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
              .replaceAll('/', '-'),
        booking_status,
        booking_time,
        guest_count,
        guest_notes,
        guest_status,
        table_booking_id: uuidv4(),
        table_number,
        tags,
        place_id: placeId,
        service_type_setting_id:
          props.tableSettingProps.service_type_setting_id,
        app_type: platform,
        user_role: props.userRole,
      };
      const res = await createTableBooking(input);
      if (!res.isError) {
        // table_booking_id: tableBookingDetails.table_booking_id,
        // booking_status: 'RECEIVED',
        // payment: {
        //   payment_method: 'card',
        //   payment_method_id: payment.id,
        //   payment_method_type: 'CARD',
        // },
        // user_id: userId,

        // const { table_booking_id } = res.data;

        // props.updateTableBooking();
        swal('Successfully created table booking').then(() => {
          history.goBack();
        });
      } else {
        toast.error('Error while updating table booking');
      }
    } else {
      const { table_booking_id, user_id } = props.tableBookingDataProps;

      const input = {
        contact: {
          contact_email,
          contact_phone,
          first_name,
          last_name,
        },
        restaurant_notes,
        booking_date: values.slotDate
          ? values.slotDate
              .split('-')
              .reverse()
              .join('-')
          : new Date(booking_date)
              .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
              .replaceAll('/', '-'),
        booking_time,
        guest_count,
        guest_notes,
        table_booking_id,
        table_number,
        tags,
        user_id: user_id || props.adminUserId,
        place_id: placeId,
        service_type_setting_id:
          props.tableSettingProps.service_type_setting_id,
        app_type: platform,
        user_role: props.userRole,
        booking_status: 'MODIFIED',
        is_sms_enabled: values.smsNotif,
        is_email_enabled: values.emailNotif,
      };

      if (
        props.tableBookingDataProps.booking_status !== values.booking_status
      ) {
        if (values.booking_status === 'CANCELLED') {
          swal({
            text: 'Would you like to cancel this table booking?',
            buttons: ['Cancel', 'OK'],
          }).then(async response => {
            if (response) {
              input.booking_status = booking_status;
              const res = await props.updateTableBooking(input);
              if (!res) {
                swal('Successfully updated table booking').then(() => {
                  history.goBack();
                });
              }
            } else {
              // pass
            }
          });
        } else {
          input.booking_status = booking_status;

          const res = await props.updateTableBooking(input);

          if (!res) {
            swal('Successfully updated table booking').then(() => {
              history.goBack();
            });
          }
        }
      } else {
        const res = await props.updateTableBooking(input);

        if (!res) {
          swal('Successfully updated table booking').then(() => {
            history.goBack();
          });
        }
      }
    }
    setSubmitting(false);
  },
  displayName: 'Form',
})(Form);

const useRefState = refValue => {
  const initialiseRef = React.useRef(true);
  const [refState, setRefState] = React.useState();

  if (initialiseRef.current && refValue) {
    setRefState(refValue);
    initialiseRef.current = false;
  }
  return [refState, setRefState];
};

const useUserRole = () => {
  const { user } = useStoreState(state => state.auth);
  const rolesArray = Array.isArray(user?.role) ? user.role : [];

  let userRole = null;

  const temp = new Map();

  rolesArray.forEach(rls => {
    if (rls === 'kravein_super_admin') {
      temp.set('kravein_super_admin', true);
    } else if (rls.startsWith('kravein_')) {
      temp.set('kravein_admin', true);
    } else if (rls.startsWith('partner_')) {
      temp.set('partner_admin', true);
    }
  });

  if (temp.get('kravein_super_admin')) {
    userRole = 'kravein_super_admin';
  } else if (temp.get('kravein_admin')) {
    userRole = 'kravein_admin';
  } else if (temp.get('partner_admin')) {
    userRole = 'partner_admin';
  }

  return userRole;
};

const AddBooking = ({ match, history }) => {
  const [tableBookingId, ,] = useRefState(match.params.id);
  const [placeId, ,] = useRefState(match.params.placeId);
  const { userId: adminUserId } = useStoreState(state => state.auth);
  const [
    updatingTableBooking,
    updateTableBooking,
    reloadTableBookingData,
    updateTableBookingError,
  ] = useUpdateTableBooking();
  const [dataLoading, tableBookingData] = useTableBookingsDetails({
    adminUserId,
    tableBookingId,
    reloadTableBookingData,
  });
  const tableBookingDataProps = tableBookingData ?? null;
  const [_tableSettingProps] = useTableSetting({ placeId });
  const [tableSettingPropsPrivate] = useTableSettingPrivate({ placeId });

  const isEmailNotificationCheckboxEnabled = (() => {
    if (tableSettingPropsPrivate === null) {
      return false;
    }

    return true;
  })();

  const isSMSNotificationCheckboxEnabled = (() => {
    if (tableSettingPropsPrivate === null) {
      return false;
    }

    if (
      tableSettingPropsPrivate?.notification_setting
        ?.sms_user_confirm_booking ||
      tableSettingPropsPrivate?.notification_setting?.sms_user_cancel_booking ||
      tableSettingPropsPrivate?.notification_setting?.sms_user_modified_booking
    ) {
      return true;
    }

    return false;
  })();

  const tableSettingProps = {
    ..._tableSettingProps,
    ...tableSettingPropsPrivate,
    isEmailNotificationCheckboxEnabled,
    isSMSNotificationCheckboxEnabled,
  };

  const [, createTableBooking, ,] = useCreateTableBooking();

  const isAdd = tableBookingId === undefined;

  const userRole = useUserRole();

  if (dataLoading) {
    return (
      <Layout>
        <Header />
        loading...
      </Layout>
    );
  }
  return (
    <AddModifyBookingForm
      adminUserId={adminUserId}
      updateTableBookingError={updateTableBookingError}
      history={history}
      isAdd={isAdd}
      placeId={placeId}
      tableBookingId={tableBookingId}
      tableSettingProps={tableSettingProps}
      updatingTableBooking={updatingTableBooking}
      updateTableBooking={updateTableBooking}
      createTableBooking={createTableBooking}
      tableBookingDataProps={tableBookingDataProps}
      userRole={userRole}
    />
  );
};

export default AddBooking;
